import { useMemo } from 'react'

import { InFilterDefinition } from '@factorialco/f0-react'
import {
  CategoryExpenseFilterDocument,
  CategoryExpenseFilterQuery,
} from 'generated/resources/graphql'

import { useGraphqlDataSource } from 'modules/core/hooks/useGraphqlDataSource'
import i18n from 'modules/core/lib/i18n'

const extractConnection = (data: CategoryExpenseFilterQuery) =>
  data.finance.customCategoriesConnection

const extractNodes = (connection: ReturnType<typeof extractConnection>) =>
  connection.edges.map((edge) => edge.node)

type Node = ReturnType<typeof extractNodes>[number]

interface UseCategoryFilterOptions {
  companyId: string
  canUsePerDiems: boolean
}

export const useCategoryFilter = ({
  companyId,
  canUsePerDiems,
}: UseCategoryFilterOptions): InFilterDefinition<string> => {
  const source = useGraphqlDataSource(
    {
      paginationType: 'infinite-scroll',
      fetchPolicy: 'cache-first',
      document: CategoryExpenseFilterDocument,
      perPage: 25,
      mapData: (data) => {
        const nodes = extractNodes(extractConnection(data))
        return canUsePerDiems ? nodes : nodes.filter((n) => n.identifier !== 'per_diem')
      },
      mapPagination: (data) => {
        const connection = extractConnection(data)
        return {
          total: connection.totalCount ?? 0,
          cursor: connection.pageInfo.endCursor ?? undefined,
          hasNextPage: !!connection.pageInfo.hasNextPage,
        }
      },
      mapVariables: ({ search }) => ({ search, companyIds: [companyId] }),
    },
    [companyId, canUsePerDiems]
  )

  return useMemo(
    () => ({
      label: i18n.t('expenses_v2.list.header.category'),
      type: 'in' as const,
      options: {
        source,
        mapOptions: (cat: Node) => ({
          label: cat.label,
          value: cat.identifier,
        }),
      },
    }),
    [source]
  )
}
